'use client'

import { useState } from 'react'
import Link from 'next/link'
import { Menu, X, Server, Zap } from 'lucide-react'
import { SignedIn, SignedOut, UserButton } from '@clerk/nextjs'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

const NAV_LINKS = [
  { href: '/pricing', label: 'Harga' },
  { href: '/#features', label: 'Fitur' },
  { href: '/#faq', label: 'FAQ' },
]

/**
 * Navbar — public site navigation.
 * Responsive: hamburguer drawer < md.
 * ZERO raw hex — all token classes.
 */
export function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false)

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-bg-base/80 backdrop-blur-md">
      <nav
        className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8"
        aria-label="Navigasi utama"
      >
        {/* Logo */}
        <Link
          href="/"
          className="flex items-center gap-2 text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 rounded-md"
          aria-label="NoctrHost — kembali ke beranda"
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-violet-700">
            <Server className="h-4 w-4 text-white" aria-hidden="true" />
          </div>
          <span className="font-display font-bold text-body-lg tracking-tight">
            Noctr<span className="text-violet-400">Host</span>
          </span>
        </Link>

        {/* Desktop nav links */}
        <ul className="hidden md:flex items-center gap-6" role="list">
          {NAV_LINKS.map(({ href, label }) => (
            <li key={href}>
              <Link
                href={href}
                className={cn(
                  'text-body-sm font-sans text-text-muted',
                  'transition-colors duration-fast hover:text-text-primary',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 rounded-sm',
                )}
              >
                {label}
              </Link>
            </li>
          ))}
        </ul>

        {/* Auth CTA */}
        <div className="hidden md:flex items-center gap-3">
          <SignedOut>
            <Button variant="ghost" size="sm" asChild>
              <Link href="/sign-in">Masuk</Link>
            </Button>
            <Button variant="primary" size="sm" asChild>
              <Link href="/sign-up">
                <Zap className="h-3.5 w-3.5" aria-hidden="true" />
                Mulai Gratis
              </Link>
            </Button>
          </SignedOut>
          <SignedIn>
            <Button variant="secondary" size="sm" asChild>
              <Link href="/dashboard">Dashboard</Link>
            </Button>
            <UserButton afterSignOutUrl="/" />
          </SignedIn>
        </div>

        {/* Mobile burger */}
        <button
          className="md:hidden p-2 text-text-muted hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 rounded-md"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-expanded={mobileOpen}
          aria-controls="mobile-menu"
          aria-label={mobileOpen ? 'Tutup menu' : 'Buka menu'}
        >
          {mobileOpen
            ? <X className="h-5 w-5" aria-hidden="true" />
            : <Menu className="h-5 w-5" aria-hidden="true" />
          }
        </button>
      </nav>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          id="mobile-menu"
          className="md:hidden border-t border-border bg-bg-s1 px-4 py-4"
          role="dialog"
          aria-label="Menu mobile"
        >
          <ul className="flex flex-col gap-2" role="list">
            {NAV_LINKS.map(({ href, label }) => (
              <li key={href}>
                <Link
                  href={href}
                  className="block py-2 text-body text-text-muted hover:text-text-primary transition-colors duration-fast rounded-md px-2"
                  onClick={() => setMobileOpen(false)}
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>
          <div className="mt-4 flex flex-col gap-2">
            <SignedOut>
              <Button variant="secondary" size="md" className="w-full" asChild>
                <Link href="/sign-in" onClick={() => setMobileOpen(false)}>Masuk</Link>
              </Button>
              <Button variant="primary" size="md" className="w-full" asChild>
                <Link href="/sign-up" onClick={() => setMobileOpen(false)}>Mulai Gratis</Link>
              </Button>
            </SignedOut>
            <SignedIn>
              <Button variant="primary" size="md" className="w-full" asChild>
                <Link href="/dashboard" onClick={() => setMobileOpen(false)}>Dashboard</Link>
              </Button>
            </SignedIn>
          </div>
        </div>
      )}
    </header>
  )
}
